/*!
TO-DONE
- Modal for How to
- First time scanning issues
- Tweaking out on every scan first time through?
- Protect against scanning a the wrong type of note
- Test in an EMR
- Ability to Delete unwanted examples
- Make the AI actually work
- Needs to track both content and style and format seems like just content now
- Fix Hallucinations in the Generation
TODO
BACKEND
- See how good the actual result is

- Check emrQuery to see if they already have a template for this pageInfo and if so tell them we have disabled them so that this one will take effect
- Backend/frontend protect them from overwriting their existing template unless they really want to


- Iterate on the result
- Make the Plus Button Work
- ?? Make it work in web App ??

*/

import { Card, CardHeader, Container, Row, Button } from 'reactstrap';
import { GlobalContext } from '../api/GlobalContext';
import { useContext, useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ConfirmationModal from '../components/Misc/ConfirmationModal.js';
import { CollapsibleCore } from '../components/Misc/Collapsible';
import EditableLabel from '../components/Misc/EditableLabel';
// import the isCX function from the api/GlobalContext.js file
import { isCX } from '../api/DeviceInfo';
import { fetchUserConfigs } from '../components/Misc/apiUtils';
import { getEmrNoteTypeValues } from '../components/Misc/utils';
import Modal from '../components/Modals/Modal.js';
import AddSectionModal from '../components/Modals/BuilderSectionNoteModal.js';
import VideoModal from '../components/Modals/VideoModal'; // Import the VideoModal component

// core components
import Header from 'components/Headers/Header';
import HashState from '../variables/hash';
import {
  getHaveInjector,
  getPageInfo,
  getQuestionsFromEMR,
} from '../EMR_Integration/react_webInjector.ts';

const FAILED_GENERATION_MESSAGE =
  'Generation is taking longer than expected. Please wait 2 minutes and give it a try with a new visit!';
const N_EXAMPLES = 3;
const MIN_ONE_SECTION_EXAMPLES = 3;
let INTRO = (
  <>
    <p>
      <strong>
        With <em>at least</em> 3 completed notes
      </strong>{' '}
      to match your style!
    </p>

    <p>
      <strong>Step 1:</strong> Open your EMR to a completed note (not a PDF)
    </p>
    <p style={{ marginLeft: '20px' }}>
      - The note must be <strong>unsigned or editable</strong> for our system to
      sync it
    </p>

    <p>
      <strong>Step 2:</strong> Click ‘Sync’
    </p>

    <p>
      <strong>Step 3:</strong> Repeat with at least 2 other notes
    </p>
  </>
);
let WEBAPPINTRO = `Welcome to the Template Builder! Get started by adding sections and uploading at least ${MIN_ONE_SECTION_EXAMPLES} example notes per section. This will train the AI to capture your unique style and tone, allowing it to generate notes that align perfectly with your preferences.`;
let addedTabsMessage = false;
let hasShownInsufficientExamplesModal = false;

const TemplateBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Default Values for the fields
  const defaultName = 'Template Name';

  // Get our Globals
  const { apiClient } = useContext(GlobalContext);

  // State Variables
  const hash = HashState();
  const [configID, setConfigID] = useState(parseInt(hash, 10)); // This is the ID of the conversation we are looking at
  const [name, _setName] = useState(defaultName);
  const [sectionData, setSectionData] = useState({});
  const [status, setStatus] = useState('Welcome to the Template Builder.');
  const [emrUrl, setEmrUrl] = useState('');
  const [emrNoteTypes, setEmrNoteTypes] = useState([]);
  const [fetchDataTrigger, setFetchDataTrigger] = useState(0);
  const [hasCheckedConfigID, setHasCheckedConfigID] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalText, setModalText] = useState('');
  const [modalHeader, setModalHeader] = useState('Template Builder');
  const [modalButtons, setModalButtons] = useState('');
  const [haveInjector, setHaveInjector] = useState();
  const [canGenerateTemplate, setCanGenerateTemplate] = useState(false);
  const [showSectionInstructionsButton, setShowSectionInstructionsButton] =
    useState(false);
  const [confirmationDetails, setConfirmationDetails] = useState({
    isOpen: false,
    key: null,
    exampleIndex: null,
  });
  const [isAddSectionModalOpen, setIsAddSectionModalOpen] = useState(false);
  const [isVideoModalOpen, setVideoModalOpen] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(true); // Track if it's the first time showing wrong type of note modal

  const toggleVideoModal = () => {
    setVideoModalOpen(!isVideoModalOpen);
  };

  const isMountedRef = useRef(true);

  useEffect(() => {
    // Set isMountedRef to true when the component mounts
    isMountedRef.current = true;

    // Set isMountedRef to false when the component unmounts
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchInjector = async () => {
      try {
        const haveInjector = await getHaveInjector();
        setHaveInjector(haveInjector);
      } catch (error) {
        console.error('Error fetching injector:', error);
      }
    };
    fetchInjector();
  }, []);

  // Functions to update the state variables (and send to the server)
  const setName = async (newName) => {
    try {
      _setName(newName);
      if (configID) {
        let configDetails = await apiClient.editConfig(
          configID,
          'name',
          '',
          newName,
        );

        if (
          'error' in configDetails &&
          isMountedRef.current && //showing the alert only when user is still on the page
          configDetails.error === 'New config name already exists'
        ) {
          alert('Template with same name exists. Please try a different name.');
          return;
        }
      }
    } catch (error) {
      console.error('Error editing config:', error);
    }
  };

  const openConfirmationModal = (type, questionKey, index) => {
    setConfirmationDetails({ isOpen: true, type, questionKey, index });
  };

  const handleConfirmAction = () => {
    if (confirmationDetails.type === 'section') {
      const updatedSectionData = { ...sectionData };
      const keyToDelete = confirmationDetails.key;

      delete updatedSectionData[keyToDelete];
      setSectionData(updatedSectionData);
      apiClient.editConfig(
        configID,
        'remove_training_data_question',
        keyToDelete,
        {},
      );
    } else {
      const { key, exampleIndex } = confirmationDetails;
      if (key !== null && exampleIndex !== null) {
        const updatedExamples = [...sectionData[key]];
        updatedExamples.splice(exampleIndex, 1);
        apiClient.editConfig(configID, 'training_data', key, updatedExamples);
        setSectionData((prevData) => ({
          ...prevData,

          [key]: updatedExamples,
        }));
      }
    }
    setConfirmationDetails({ isOpen: false, key: null, exampleIndex: null });
  };

  useEffect(() => {
    // Reset the state variables
    _setName(defaultName);
    setSectionData({});

    if (hash === '') {
      console.log('No config id specified assuming new');
      window.history.pushState({}, '', `#new`);
      return;
    }
    setConfigID(parseInt(hash, 10));
  }, [hash]);

  // Check if every section has N_EXAMPLES or more examples we can generate
  useEffect(() => {
    const allSectionsMeetMinimumRequirements = Object.values(sectionData).every(
      (examples) => examples.length >= N_EXAMPLES,
    );

    const atLeastOneSectionMeetsThreshold = Object.values(sectionData).some(
      (examples) => examples.length >= MIN_ONE_SECTION_EXAMPLES,
    );

    setCanGenerateTemplate(
      allSectionsMeetMinimumRequirements && atLeastOneSectionMeetsThreshold,
    );
  }, [sectionData]);

  // The status should show how close we are to being able to generate a template
  useEffect(() => {
    if (canGenerateTemplate) {
      setStatus('Time to generate your template!');
    } else {
      const numExamples =
        Object.values(sectionData).length > 0
          ? Math.min(
              ...Object.values(sectionData).map((examples) => examples.length),
            )
          : 0;
      if (numExamples === 0) {
        //If CX, show the message to scan the EMR else show the message to upload examples
        haveInjector
          ? setStatus(
              `Scan your EMR to get started! You need at least ${N_EXAMPLES} examples in each section to generate a template.`,
            )
          : setStatus(
              `Add Section to get started! You need at least ${N_EXAMPLES} examples in each section to generate a template.`,
            );
      } else if (numExamples >= MIN_ONE_SECTION_EXAMPLES) {
        setStatus(
          `${numExamples} of ${MIN_ONE_SECTION_EXAMPLES} examples loaded.`,
        );
      } else {
        setStatus(
          `${numExamples} of ${MIN_ONE_SECTION_EXAMPLES} examples loaded. You need at least ${N_EXAMPLES} examples in each section to generate a template.`,
        );
      }
    }
  }, [canGenerateTemplate, sectionData, haveInjector]);

  // On load, get the config details
  useEffect(() => {
    const fetchData = async () => {
      try {
        let pageInfo = haveInjector
          ? await getPageInfo()
          : { URL: 'app.comprehendpt.com', 'Note Type': 'Unsupported in App' }; // hardCoding here as get pageInfo is not responding in webApp (tried a lot of things but no luck)

        let url = pageInfo['URL'];
        setEmrUrl(url);
        const now = new Date();
        const date = now.toISOString().split('T')[0]; // Format: YYYY-MM-DD
        const time = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // Format: HH-MM-SS
        const timestamp = `${date}-${time}`;
        // if !CX note type is Unsupported in App
        let noteType =
          pageInfo['Note Type'] === 'Unsupported in App'
            ? `Web App Edition (${timestamp})`
            : pageInfo['Note Type'];

        if (!url || !noteType) {
          console.log('Error getting pageInfo');
          setModalOpen(true);
          setModalText(
            'Error getting pageInfo, please try again (refresh the page).',
          );
          return;
        }
        let builderTemplateDetails;
        //If cx we have url and noteType else we have builderId
        if (haveInjector) {
          builderTemplateDetails = await apiClient.getBuilderConfig(
            '',
            url,
            noteType,
          );
        } else {
          const url = window.location.href;
          const match = url.match(/builder#(new|\d+)/);
          const builderId = match ? match[1] : null;
          if (builderId !== 'new') {
            builderTemplateDetails = await apiClient.getBuilderConfig(
              parseInt(builderId),
              '',
              '',
            );
          }
        }

        const localConfigID = builderTemplateDetails?.configId;
        if (!isNaN(localConfigID)) {
          // If we have a valid configID, get the details
          setStatus('Loading template details...');
          const data = await apiClient.getConfigDetails(localConfigID);
          console.log(data);
          if (data.error) {
            console.log('Error getting config details' + localConfigID);
            return;
          }
          // only set the values if the field is not null or undefined
          _setName(data.name || defaultName);
          let emrUrl = data.emr_url || '';
          setEmrUrl(emrUrl || '');
          setEmrNoteTypes(getEmrNoteTypeValues(data.emr_note_type));
          // Set the training data
          setSectionData(data.training_data || {});
          setConfigID(localConfigID);

          if (!emrUrl.includes('Disabled')) {
            setShowSectionInstructionsButton(true);
            setModalOpen(true);
            setModalText(
              'Looks like you already used template builder previously and generated template with instructions! Once you click "Overwrite Instructions" you can no longer retrieve older instructions. If you\'d rather make a new template you can do that too!',
            );
            setModalButtons([
              {
                label: `Overwrite instructions`,
                color: 'grey',
                onClick: () => {},
              },
              {
                label: `Create New`,
                color: 'secondary',
                onClick: () => {
                  createTemplate(true);
                },
              },
              {
                label: `View Template`,
                color: 'primary',
                onClick: () => {
                  navigate(`/User/templates/manager#${localConfigID}`);
                },
              },
            ]);
          } else if (
            emrUrl.includes('Disabled') &&
            Object.keys(data.samples).length > 0
          ) {
            setModalOpen(true);
            setModalText(
              'Seems like a template already exists but it is disabled. Go to existing template and re-enable it. Select "Modify" to update your examples and generate new instructions.',
            );
            setModalButtons([
              {
                label: `Modify`,
                color: 'grey',
                onClick: () => {},
              },
              {
                label: `Go to existing template`,
                color: 'primary',
                onClick: () => {
                  navigate(`/User/templates/manager#${localConfigID}`);
                },
              },
            ]);
          }
        } else {
          // This is a new template
          console.log('configID is bad', localConfigID);
          setModalOpen(true);
          if (url.includes('empoweremr.com') || url.includes('app.webpt.com')) {
            if (!addedTabsMessage) {
              // INTRO += ' Please make sure you scan all the tabs in the EMR.';
              addedTabsMessage = true; // Set flag to true after appending the message
            }
          }
          setModalHeader('Train Comprehend!');
          setModalText(haveInjector ? INTRO : WEBAPPINTRO);
          setModalButtons([
            {
              label: `Let's Begin!`,
              color: 'primary',
              onClick: () => {},
            },
          ]);
        }
        setHasCheckedConfigID(true);
      } catch (error) {
        console.error('Error fetching data:', error);
        setModalOpen(true);
        setModalText(
          'An error occurred while fetching data. Please try again.',
        );
      }
    };
    fetchData();
  }, [fetchDataTrigger, haveInjector]);

  // Help Button
  const openHelpModal = () => {
    setModalOpen(true);
    setModalText(
      'After working with hundreds of clinicians we found that every one of them had their own way of documenting so we built templates to allow you to customize your notes. What we found was instructions and examples were cumbersome and confusing, making onboarding difficult and time consuming for new users. We built the template builder to allow you to create your own templates just by loading in your existing notes. We use AI to analyze your existing notes and write your instructions for you.',
    );
  };

  // If the hash is new, create a new template and name it based on the pageInfo
  async function createTemplate(forceCreate = false) {
    let pageInfo = haveInjector
      ? await getPageInfo()
      : { URL: 'app.comprehendpt.com', 'Note Type': 'Unsupported in App' }; // hardCoding here as get pageInfo not responding in webApp (tried a lot of things but no luck)

    let userConfigs = await fetchUserConfigs(apiClient);

    const now = new Date();
    const date = now.toISOString().split('T')[0]; // Format: YYYY-MM-DD
    const time = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // Format: HH-MM-SS
    const timestamp = `${date}-${time}`;

    // Set note type with a shorter name, adding (1), (2), etc. if there are duplicates
    let noteTypeBase =
      pageInfo['Note Type'] === 'Unsupported in App'
        ? `Web App Edition-${timestamp}`
        : pageInfo['Note Type'];
    noteTypeBase = noteTypeBase.replace(/Signed|Original/gi, '').trim(); // Remove "Signed" or "Original"

    let noteType = noteTypeBase;

    if (!haveInjector) {
      // Filter existing configurations to check for duplicates
      let existingConfigs = userConfigs?.filter((config) =>
        config.name.includes(noteTypeBase),
      );

      // If a duplicate exists, append a number to the name
      if (existingConfigs.length > 0) {
        let highestNumber = 0;

        // Find the highest number appended to the duplicates
        existingConfigs.forEach((config) => {
          const match = config.name.match(/\((\d+)\)$/); // Check for (1), (2), etc. at the end
          if (match) {
            const number = parseInt(match[1], 10);
            if (number > highestNumber) {
              highestNumber = number;
            }
          }
        });

        // Append the next number to the name
        noteType = `${noteTypeBase} (${highestNumber + 1})`;
      }
    }

    // Create the final note name without "Builder"
    let noteName = `Builder - ${noteType && noteType.length > 0 ? noteType : noteTypeBase}`;
    if (!pageInfo.URL || !noteType) {
      console.log('Error getting pageInfo');
      setModalOpen(true);
      setModalText(
        'Error getting pageInfo, please try again (refresh the page).',
      );
      return undefined;
    }
    setEmrUrl(pageInfo.URL);
    setEmrNoteTypes([noteType]);

    setStatus(`Creating a new ${noteType} template...`);

    // If we want the user to create a new builder they can
    let builderTemplateDetails;
    if (!forceCreate) {
      builderTemplateDetails = await apiClient.getBuilderConfig(
        '',
        pageInfo.URL,
        noteType,
      );
    } else {
      builderTemplateDetails = null;
      // Clear the samples (It still as the ond ones for some reason...)
      setSectionData({});
    }

    let configIDLocal = builderTemplateDetails?.configId;

    if (!configIDLocal) {
      const newTemplate = await apiClient.newTemplate();
      configIDLocal = newTemplate['configId'];
      // Change the template name, EMRurl, and EMR Note type to that of the pageInfo
      await apiClient.editConfigBulk(configIDLocal, [
        { fieldName: 'emr_url', value: 'Disabled - ' + pageInfo.URL, key: '' },
        { fieldName: 'emr_note_type', value: noteType, key: '' },
        { fieldName: 'name', value: noteName, key: '' },
      ]);
    }

    // Set the hash in a non reloading way in case they refresh
    window.history.pushState({}, '', `#${configIDLocal}`);
    setConfigID(configIDLocal);
    setName(noteName);
    setStatus(
      `Upload examples to get started! You need at least ${N_EXAMPLES} examples in each section to generate a template.`,
    );
    return configIDLocal;
  }

  // Scan Template Button - Look at the EMR and pull in the existing notes
  async function scrapeTemplate() {
    let configIDLocal = configID;
    // this is needed when Template builder is loaded but the EMR is not. When we hit scan, first populate the exisiting template details
    if (!hasCheckedConfigID && isNaN(configIDLocal)) {
      setFetchDataTrigger((prev) => prev + 1);
      return;
    }
    if (((hash === 'new') | (hash === '')) & isNaN(configIDLocal)) {
      console.log(
        `Creating a new template - Hash is new and configId is Nan... (${hash}), (${configIDLocal})`,
      );
      configIDLocal = await createTemplate();
      if ((configIDLocal === -1) | !configIDLocal) {
        console.log('Error creating a new template');
        setModalOpen(true);
        setModalText('Error creating a new template, please try again.');
        return;
      }
    } else {
      // Verify this is the right type of note
      const emrDetails = await getPageInfo();
      if (emrUrl.includes('Disabled')) {
        emrDetails.URL = 'Disabled - ' + emrDetails.URL;
      }
      if (
        emrUrl !== emrDetails.URL ||
        !emrNoteTypes.includes(emrDetails['Note Type'])
      ) {
        const expectedNoteTypes = emrNoteTypes.join(', ');
        console.log(
          `Wrong type of note, expected ${expectedNoteTypes} but got ${emrDetails['Note Type']}`,
        );
        if (isFirstTime) {
          setModalOpen(true);
          setModalHeader('⚠️ Note Type Mismatch Detected');
          setModalText(
            `Scanned '${emrDetails['Note Type']}' instead of '${expectedNoteTypes}'. Please maintain consistency in note types to ensure accurate and focused instructions. Combining different note types will result in a single template with sections from both, which may lead to confusion.`,
          );
          setModalButtons([
            {
              label: `Cancel`,
              color: 'grey',
              onClick: () => {},
            },
            {
              label: `Proceed Anyway`,
              color: 'primary',
              onClick: () => {},
            },
          ]);
          setIsFirstTime(false); // Update the state to indicate the modal has been shown
          return; // Prevent further action
        }
      }
    }

    // Get the questions from the EMR and throw them into the sectionData (training date)
    let scrapedData = await getQuestionsFromEMR(false, {}, {}, false);
    console.log('Scraped Data', scrapedData);

    // Custom setter that will update the sectionData and canGenerateTemplate
    let updatedData = { ...sectionData }; // Copy previous data
    scrapedData.forEach((item) => {
      // handling for flex
      if (!item.ExistingNote && item.Structure) {
        item.ExistingNote = item.Structure;
      }
      // Check if the section key already exists in updatedData
      if (item.Key in updatedData) {
        // If the section key exists, push the ExistingNote to the array of examples
        // console.log("item", item)
        updatedData[item.Key] = [...updatedData[item.Key], item.ExistingNote];
      } else {
        // If the section key does not exist, create a new section with the ExistingNote if provided
        if (item.ExistingNote) {
          updatedData[item.Key] = [item.ExistingNote];
        } else {
          console.log('No ExistingNote for', item.Key);
        }
      }
    });
    console.log('Updated Data', updatedData);
    const sortedData = Object.entries(updatedData)
      .sort(([, a], [, b]) => a.length - b.length)
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});
    setSectionData(sortedData);

    // Push the array to the server
    let editBulk = [];
    for (const [key, value] of Object.entries(updatedData)) {
      editBulk.push({ fieldName: 'training_data', key: key, value: value });
    }
    const rsp = await apiClient.editConfigBulk(configIDLocal, editBulk);
    console.log('Edit Bulk', rsp);
  }

  // Add Section Button - Add a new section to the template
  const addSection = async (sectionName) => {
    if (!sectionName) {
      return;
    }

    // Add the section to the sectionData and sort the previous data
    setSectionData((prevData) => {
      // Sort the previous data by the number of examples in ascending order
      const sortedData = Object.entries(prevData)
        .sort(([, a], [, b]) => a.length - b.length)
        .reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {});

      // Return the new section at the top, followed by the sorted previous data
      return {
        [sectionName]: [],
        ...sortedData,
      };
    });
    let configIDLocal;
    if (!configID || isNaN(configID)) {
      configIDLocal = await createTemplate();
      if (!configIDLocal) {
        console.error('Failed to create template. Aborting section addition.');
        return;
      }
    }

    // Add the section to the server
    try {
      await apiClient.editConfig(
        configIDLocal,
        'training_data',
        sectionName,
        [],
      );
    } catch (error) {
      console.error('Failed to add section to the server:', error);
    }
  };

  // Generate Template Button - Generate the template based on the examples
  async function genTemplate(section) {
    if (typeof section === 'object') {
      section = null;
    }
    if (!canGenerateTemplate) {
      console.log('Not enough examples to generate a template');
      return;
    }

    try {
      const details = await apiClient.getConfigDetails(configID);
      console.log('Details', details);
      const keysWithLessThanMinimumRequirements = Object.keys(
        details?.training_data || {},
      ).filter(
        (key) =>
          (details?.training_data[key]?.filter((item) => item !== null)
            .length || 0) < N_EXAMPLES,
      );
      console.log(
        'Keys with less than minimum requirements',
        keysWithLessThanMinimumRequirements,
      );
      console.log(hasShownInsufficientExamplesModal);
      if (
        keysWithLessThanMinimumRequirements.length > 0 &&
        !hasShownInsufficientExamplesModal
      ) {
        hasShownInsufficientExamplesModal = true; // Set the flag to true to avoid showing the modal again
        setModalOpen(true);
        setModalText(
          `Looks like you have less than 3 examples for ${keysWithLessThanMinimumRequirements.join(', ')}. Click "Scan" to add more examples to generate a template with better instructions else Click "Generate Template".`,
        );
        return; // Exit the function if there are insufficient examples
      }

      // Modal Popup saying Congrats! Your template is being generated now. You will be re-directed when it's ready!
      setModalOpen(true);
      setModalText(
        "Congrats! Your template is being generated now. You will be re-directed when it's ready!",
      );
      setStatus(
        'Generating your template. You will be redirected when it is ready',
      );
      setCanGenerateTemplate(false);
      console.log('Section', section);
      await apiClient.editConfig(configID, 'enable', '', true);
      console.log('Enable Config', configID);
      const rsp = await apiClient.genTemplate(configID, section);
      console.log('Generate Template', rsp);
      if (rsp['result'] === 'success') {
        // If successful, redirect them to the template page
        navigate(`/User/templates/manager#${configID}`);
      } else {
        setModalText(FAILED_GENERATION_MESSAGE);
      }
    } catch (error) {
      // I seem to get {message: "Endpoint request timed out"}, I tried to use that error with no luck, but the server error does work.
      if (apiClient.isTimeoutError(error)) {
        genTimeoutKeepTrying();
      } else {
        setModalText(FAILED_GENERATION_MESSAGE);
      }
    }
  }

  function genTimeoutKeepTrying() {
    // If failure, check if it's because we got no response - timeout - in which case every 15 seconds get details and see if there is something good in the instructions, do this for up to 2 minutes.
    const status_update =
      "It's taking a little longer than expected to generate your template. Stay Tuned (It shouldn't take more than 2 minutes)!";
    setModalText(status_update);
    setStatus(status_update);
    let count = 0;
    let interval = setInterval(async () => {
      count += 1;
      const details = await apiClient.getConfigDetails(configID);
      console.log('Details', details);
      // It's done when all but 1 of the sections have instructions
      if (
        details.questions &&
        Object.keys(details.questions).length >=
          Object.keys(sectionData).length - 1
      ) {
        clearInterval(interval);
        navigate(`/User/templates/manager#${configID}`);
      }
      if (count >= 8) {
        clearInterval(interval);
        setModalText(FAILED_GENERATION_MESSAGE);
      }
    }, 15000);
  }

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalButtons([]); // Reset modalButtons to an empty array
    setModalHeader('Template Builder');
  };

  const fullPath = window.location.href;
  const userPath = fullPath.substring(fullPath.indexOf('/User'));
  localStorage.setItem('lastVisitedURL', userPath);
  const urls = {
    'go.promptemr.com':
      'https://docs.google.com/document/d/1NMne2bcCUNOLJS9rhS_v3xmd7RE1wY6BRu3ZkQG9VSM/edit?usp=sharing',
    'app.webpt.com':
      'https://docs.google.com/document/d/1Ja_SS2MYznuwwJGgjeh-g-fziuFllmwiEYlShhzoQjM/edit?usp=sharing',
    'emr.webpt.com':
      'https://docs.google.com/document/d/1Ja_SS2MYznuwwJGgjeh-g-fziuFllmwiEYlShhzoQjM/edit?usp=sharing',
  };

  const handleButtonClick = () => {
    for (const key in urls) {
      if (emrUrl.includes(key)) {
        window.open(
          urls[key].replace('/edit?usp=sharing', '/preview'),
          '_blank',
        );
        return;
      }
    }
    setVideoModalOpen(true);
  };
  return (
    <>
      <Header isLite={true} />
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        buttons={modalButtons}
      >
        <h2>{modalHeader}</h2>
        {/*changing this to div as p tag will not work for intro*/}
        <div>{modalText}</div>
      </Modal>
      {/* Page content */}
      <Container className="mt--9" fluid>
        {/* Table */}
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h1 className="mb-0">Template Builder</h1>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <Link
                      to={`/User/templates?tab=${location.state?.tab || 'personal'}`}
                    >
                      <Button
                        className="fa fa-arrow-left"
                        color="primary"
                        size="md"
                        title="All Templates"
                      ></Button>
                    </Link>
                  </div>
                  <div>
                    <EditableLabel
                      labelText="Name"
                      text={name}
                      onChange={setName}
                      className="h3"
                      disabled={false}
                    />
                  </div>
                  <div style={{ marginTop: '10px', textAlign: 'left' }}>
                    <Button
                      color="primary"
                      size="sm"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      onClick={handleButtonClick}
                    >
                      <span style={{ marginRight: '5px' }}>
                        <span className="fa fa-question-circle fa-2x" />
                      </span>
                      Help
                    </Button>
                    <VideoModal
                      isOpen={isVideoModalOpen}
                      onClose={toggleVideoModal}
                      isCX={isCX()}
                    />
                  </div>
                </div>
                <div style={{ margin: '20px', textAlign: 'center' }}>
                  {status}
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  {haveInjector ? (
                    <Button
                      color="primary"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      onClick={scrapeTemplate}
                      id="scrape-button"
                    >
                      <span style={{ marginRight: '5px' }}>
                        <span className="fa fa-magnifying-glass-chart fa-2x" />
                      </span>
                      Sync
                    </Button>
                  ) : (
                    <>
                      <Button
                        color="primary"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        onClick={() => setIsAddSectionModalOpen(true)}
                        id="add-section-button"
                      >
                        Add Section
                      </Button>
                      <AddSectionModal
                        isOpen={isAddSectionModalOpen}
                        onClose={() => setIsAddSectionModalOpen(false)}
                        onAdd={addSection}
                      />
                    </>
                  )}
                  <Button
                    className={canGenerateTemplate ? 'primary' : 'grey'}
                    color={canGenerateTemplate ? 'primary' : 'grey'}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: canGenerateTemplate ? 'pointer' : 'not-allowed',
                    }}
                    id="generate-button"
                    disabled={!canGenerateTemplate}
                    onClick={genTemplate}
                  >
                    <span style={{ marginRight: '5px' }}>
                      <span className="fa fa-file-text fa-2x" />
                    </span>
                    Generate Template
                  </Button>
                </div>
              </CardHeader>

              <div className="list-container">
                <div className="list-header" style={{ marginBottom: '10px' }}>
                  <strong></strong>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '20px',
                  }}
                ></div>
                {Object.keys(sectionData).map((key, index) => (
                  <div
                    style={{
                      margin: '10px',
                    }}
                    key={index}
                  >
                    <CollapsibleCore
                      key={index}
                      id={index}
                      label={
                        <div
                          style={{
                            margin: '10px',
                          }}
                        >
                          {key} ({sectionData[key].length}/3)
                        </div>
                      }
                      shownChildren={
                        <div style={{ display: 'flex' }}>
                          <Button className="fa fa-plus" />
                        </div>
                      }
                      innerChildren={
                        Array.isArray(sectionData[key]) ? (
                          <div>
                            {sectionData[key].map((example, exampleIndex) => (
                              <div
                                key={exampleIndex} // Use exampleIndex as the key
                                style={{
                                  marginBottom: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  width: '100%',
                                }}
                              >
                                <EditableLabel
                                  labelText={`Example ${exampleIndex + 1}`}
                                  text={example} // Use example directly since it's the example text
                                  onChange={(newText) => {
                                    const updatedExamples = [
                                      ...sectionData[key],
                                    ]; // Copy the array
                                    updatedExamples[exampleIndex] = newText; // Update the example
                                    apiClient.editConfig(
                                      configID,
                                      'training_data',
                                      key,
                                      updatedExamples,
                                    );
                                    setSectionData((prevData) => ({
                                      ...prevData,
                                      [key]: updatedExamples, // Update the section data with the updated examples
                                    }));
                                  }}
                                  disabled={false}
                                />
                                <Button
                                  className="fa fa-trash"
                                  onClick={() => {
                                    setConfirmationDetails({
                                      isOpen: true,
                                      key,
                                      exampleIndex,
                                      type: 'sample',
                                    });
                                  }}
                                />
                              </div>
                            ))}
                            {showSectionInstructionsButton && (
                              <Button
                                color={
                                  sectionData[key].length >= 3
                                    ? 'primary'
                                    : 'secondary'
                                }
                                style={{ width: '100%' }}
                                onClick={() => genTemplate(key)}
                                disabled={
                                  sectionData[key].length < 3 ||
                                  !canGenerateTemplate
                                }
                              >
                                Generate Instructions for - {key}
                              </Button>
                            )}
                          </div>
                        ) : (
                          'No samples available'
                        )
                      }
                      onAddClick={() => {
                        const updatedExamples = [...sectionData[key]]; // Copy the array
                        updatedExamples.push(''); // Add an empty example
                        apiClient.editConfig(
                          configID,
                          'training_data',
                          key,
                          updatedExamples,
                        );
                        setSectionData((prevData) => ({
                          ...prevData,
                          [key]: updatedExamples, // Update the section data with the updated examples
                        }));
                      }}
                      border={
                        Object.values(sectionData).every(
                          (examples) => examples.length === 0,
                        )
                          ? '2px solid #d5d5d5' // Default border if all sections have 0 examples
                          : sectionData[key].length < 3
                            ? '2px solid red' // Red border if less than 3 examples
                            : '2px solid #d5d5d5' // Default border for 3 or more examples
                      }
                      deleteButton={
                        <div
                          style={{
                            margin: '10px',
                          }}
                        >
                          <Button
                            className="fa fa-trash"
                            onClick={() => {
                              setConfirmationDetails({
                                isOpen: true,
                                key,
                                type: 'section', // Indicate that this is a section deletion
                              });
                            }}
                          />
                        </div>
                      }
                    />
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </Row>
      </Container>
      <ConfirmationModal
        isOpen={confirmationDetails.isOpen}
        onClose={() =>
          setConfirmationDetails({ ...confirmationDetails, isOpen: false })
        }
        onConfirm={handleConfirmAction}
      >
        {confirmationDetails.type === 'sample'
          ? 'Are you sure you want to delete this sample?'
          : 'Are you sure you want to delete this section?'}
      </ConfirmationModal>
    </>
  );
};

export default TemplateBuilder;
